import React from 'react';
import { useTranslation } from 'react-i18next';
import ButtonMC from '../../components/ButtonMC';
import useSearchBarUI from '../../hooks/inbox/ui/useSearchBarUI';
import { TextSearchMC } from './components';

/**
 * InboxSearchBarMC - Molecule Component (Presentational)
 *
 * Componente puramente presentacional para la barra de búsqueda del inbox.
 * Toda la lógica de negocio está encapsulada en useSearchBarUI hook.
 *
 * PRINCIPIOS SEGUIDOS:
 * - Componente atómico puramente presentacional
 * - Separación total de lógica de negocio y presentación
 * - useSearchBarUI maneja toda la lógica de negocio
 * - Este componente solo se encarga del renderizado
 * - Sin estado local de negocio, solo presentación
 *
 * @param {string} [searchTerm=''] - Current search term
 * @param {function} onSearchChange - Handler for search input changes
 * @param {Object} [sortConfig={ field: 'received', direction: 'desc' }] - Sort configuration { field: string, direction: 'asc'|'desc' }
 * @param {function} onSortChange - Handler for sort changes
 * @param {function} onShowFiltersScreen - Handler to toggle the filters screen
 * @param {boolean} [showFiltersScreen=false] - Whether to show the filters screen
 * @param {boolean} [isFiltersActive=false] - Whether any advanced filters are active
 */
const InboxSearchBarMC = ({
  searchTerm = '',
  onSearchChange,
  sortConfig = { field: 'received', direction: 'desc' },
  onSortChange,
  onShowFiltersScreen,
  showFiltersScreen = false,
  isFiltersActive = false,
  onToggleSidebarFilters,
  showSidebarFilters = false
}) => {
  const { t } = useTranslation('InboxMC');

  // Hook que encapsula TODA la lógica de negocio
  const {
    handleSearchChange,
    handleSortClick,
    handleFiltersToggle,
    sortIcon,
    sortTitle,
    filterTitle,
    showSortButton,
    showFilterButton,
    showCloseButton
  } = useSearchBarUI({
    searchTerm,
    onSearchChange,
    sortConfig,
    onSortChange,
    onShowFiltersScreen,
    showFiltersScreen
  });

  return (
    <div className="tw-relative tw-w-full">
      <TextSearchMC
        placeholder={t('search.placeholder')}
        defaultValue={searchTerm}
        debounceTime={300}
        onChange={handleSearchChange}
        mainContainerClassName="tw-flex-1"
        inputClassName="tw-min-h-10"
      />

      <div className="tw-flex tw-items-center tw-gap-0.5 tw-absolute tw-right-1 tw-top-1">
        {showSortButton && (
          <ButtonMC
            variant="SECONDARY"
            size="xs"
            icon={sortIcon}
            outlined
            onClick={handleSortClick}
            className="tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50"
            iconClassName="tw-text-gray-600"
            title={sortTitle}
          />
        )}
        {showFilterButton && (
          <div className="tw-relative">
            <ButtonMC
              variant="SECONDARY"
              size="xs"
              icon="filter-3-fill"
              outlined
              onClick={handleFiltersToggle}
              className="tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50"
              iconClassName="tw-text-gray-600"
              title={filterTitle}
            />
            {isFiltersActive && (
              <span className="tw-absolute tw-top-1 tw-right-1 tw-w-2.5 tw-h-2.5 tw-bg-red-500 tw-rounded-full tw-border-2 tw-border-white" style={{ transform: 'translate(10%,-10%)' }} />
            )}
          </div>
        )}
        {onToggleSidebarFilters && (
          <ButtonMC
            variant="SECONDARY"
            size="xs"
            icon={showSidebarFilters ? 'close-outline' : 'grid-outline'}
            outlined
            onClick={onToggleSidebarFilters}
            className="tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 md:tw-hidden"
            iconClassName="tw-text-gray-600"
            title={t('sidebar.title')}
          />
        )}
        {showCloseButton && (
          <ButtonMC
            variant="TEXT_PLAIN"
            icon="close-outline"
            onClick={handleFiltersToggle}
            className="tw-border-0 tw-bg-transparent hover:tw-bg-transparent !tw-pt-1"
            iconClassName="tw-text-gray-600 !tw-size-[24px]"
            title={filterTitle}
          />
        )}
      </div>
    </div>
  );
};

export default InboxSearchBarMC;
